export const MAX_PARTICIPANTS = 32;
export const MAX_NAME_LENGTH = 40;
export const MAX_TITLE_LENGTH = 200;

export type Role = 'player' | 'observer';

export interface Participant {
  readonly id: string;
  readonly name: string;
  readonly role: Role;
  readonly connected: boolean;
  readonly joinedAt: number;
}

export interface Roster {
  readonly hostId: string | null;
  readonly participants: Readonly<Record<string, Participant>>;
}

export function sanitizeName(raw: string): string {
  return raw.replace(/\s+/g, ' ').trim().slice(0, MAX_NAME_LENGTH);
}

export function nextHostId<T extends Roster>(roster: T, excludeId: string): string | null {
  const candidates = Object.values(roster.participants)
    .filter((participant) => participant.id !== excludeId)
    .sort((a, b) => {
      if (a.connected !== b.connected) return a.connected ? -1 : 1;
      if (a.role !== b.role) return a.role === 'player' ? -1 : 1;
      if (a.joinedAt !== b.joinedAt) return a.joinedAt - b.joinedAt;
      return a.id.localeCompare(b.id);
    });
  return candidates[0]?.id ?? null;
}

export function reelectHost<T extends Roster>(roster: T, vacatingId: string): T {
  if (roster.hostId !== vacatingId) return roster;
  return { ...roster, hostId: nextHostId(roster, vacatingId) };
}

export type JoinError = 'invalid_name' | 'room_full';
export type JoinResult<T> = { readonly ok: true; readonly roster: T } | { readonly ok: false; readonly error: JoinError };

export function applyJoin<T extends Roster>(roster: T, actorId: string, rawName: string, role: Role, now: number): JoinResult<T> {
  const name = sanitizeName(rawName);
  if (name.length === 0) return { ok: false, error: 'invalid_name' };

  const existing = roster.participants[actorId];
  if (existing) {
    const participants = { ...roster.participants, [actorId]: { ...existing, name, connected: true } };
    const rejoined: T = { ...roster, participants };
    return { ok: true, roster: rejoined.hostId === null ? { ...rejoined, hostId: actorId } : rejoined };
  }

  if (Object.keys(roster.participants).length >= MAX_PARTICIPANTS) return { ok: false, error: 'room_full' };

  const participant: Participant = { id: actorId, name, role, connected: true, joinedAt: now };
  return {
    ok: true,
    roster: { ...roster, hostId: roster.hostId ?? actorId, participants: { ...roster.participants, [actorId]: participant } },
  };
}

export function applyLeave<T extends Roster>(roster: T, actorId: string): T {
  const participants = { ...roster.participants };
  delete participants[actorId];
  return reelectHost({ ...roster, participants }, actorId);
}

export function applyDisconnect<T extends Roster>(roster: T, actorId: string): T {
  const existing = roster.participants[actorId];
  if (!existing) return roster;
  const participants = { ...roster.participants, [actorId]: { ...existing, connected: false } };
  return reelectHost({ ...roster, participants }, actorId);
}

export function applySetRole<T extends Roster>(roster: T, actorId: string, role: Role): T {
  const existing = roster.participants[actorId];
  if (!existing || existing.role === role) return roster;
  return { ...roster, participants: { ...roster.participants, [actorId]: { ...existing, role } } };
}
